
import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon } from '@/components/icons';
import { Pencil, Eye, FolderIcon } from 'lucide-react';
import { usePressReleases, usePressReleaseStats, useDeletePressRelease, useUpdatePressReleaseStatus, useSearchPressReleases } from '@/lib/api/hooks/press-release';
import { showToast } from '@/lib/toast';
import { Tab, Button, ActionDropdown, TanStackTable, Pagination, type ColumnDef, DeleteModal, UnpublishModal, PublishModal, type TabItem, type DropdownAction } from '@/components/ui';
import type { PressRelease as PressReleaseType } from '@/lib/api/types';
import PressReleaseStats from './PressReleaseStats';
import formatDate from '@/utils/helpers/formatDate';
import { initialsCase } from '@/utils/helpers/textTransform';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { usePressReleaseFilterSections } from '@/pages/articles-pages/press-release/FilterModule/FILTER_SECTIONS';

const PressRelease: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPressRelease, setSelectedPressRelease] = useState<{ id: string; title: string } | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatusUpdate, setSelectedStatusUpdate] = useState<{ id: string; title: string; currentStatus: 'DRAFT' | 'PUBLISH'; newStatus: 'DRAFT' | 'PUBLISH' } | null>(null);

  const { data: statsData } = usePressReleaseStats();
  const stats = statsData || { total: 0, totalPublished: 0, totalDrafts: 0 };

  const FILTER_SECTIONS = usePressReleaseFilterSections();
        // ── URL-driven search / filter state ──────────────────────────────────────
        const {
          mode,
          searchTerm,
          filterState,
          page,
          size,
          setSearch,
          setFilters,
          removeFilter,
          clearFilters,
          setPage,
          apiFilters,
          getActiveFilterCount
        } = useSearchFilterParams(FILTER_SECTIONS)
      
    
    
      // Search API (when searching)
      const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchPressReleases(
        { page, size },
      searchTerm,
        mode === 'search' 
      )
    
      // Filter API (when filtering or default)
      const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = usePressReleases(
        { page, size },
        {   status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT', ...apiFilters },
      )
    
      // Select appropriate data source: search takes priority
      const aggregateData = mode === 'search' ? searchData : filterData
      const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
      const isError = mode === 'search' ? isSearchError : isFilterError
    
      const totalPages = aggregateData?.data?.totalPage ?? 1
      const activeFilterCount = getActiveFilterCount()
      const refetchData = mode === 'search' ? refetchSearch : refetchFilter
      const emptyMessage = mode === 'search'
        ? 'No press releases found matching your search criteria.'
        : mode === "filter" ?  'No press releases found matching your filter criteria.'
        : 'No press releases found.'
  
         const selectedData = Array.isArray(aggregateData?.data?.data) ? aggregateData.data.data : [];

  const targetStatus: 'PUBLISH' | 'DRAFT' = activeTab === 'published' ? 'PUBLISH' : 'DRAFT';
  const pressReleases = selectedData.filter(
    (item: PressReleaseType) => item.status === targetStatus
  );


  const { mutate: deletePressRelease, isPending: isDeleting } = useDeletePressRelease();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdatePressReleaseStatus();

  const publishedCount = stats.totalPublished;
  const draftsCount = stats.totalDrafts;


  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];


  const handleEdit = useCallback((pressReleaseId: string) => {
    navigate(`/admin/press-release/${pressReleaseId}/edit`);
  }, [navigate]);


  const handleDeleteClick = useCallback((pressRelease: { pressReleaseId: string; title: string }) => {
    setSelectedPressRelease({ id: pressRelease.pressReleaseId, title: pressRelease.title });
    setDeleteModalOpen(true);
  }, []);

  // Handle status update click
  const handleStatusUpdateClick = useCallback((pressRelease: PressReleaseType) => {
    const newStatus = pressRelease.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
    setSelectedStatusUpdate({
      id: pressRelease.pressReleaseId,
      title: pressRelease.title,
      currentStatus: pressRelease.status,
      newStatus
    });
    setStatusModalOpen(true);
  }, []);


  const confirmDelete = () => {
    if (!selectedPressRelease) return;

    deletePressRelease(selectedPressRelease.id, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Press release deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedPressRelease(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  // Confirm status update
  const confirmStatusUpdate = () => {
    if (!selectedStatusUpdate) return;

    updateStatus(
      { pressReleaseId: selectedStatusUpdate.id, status: selectedStatusUpdate.newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              selectedStatusUpdate.newStatus === 'PUBLISH'
                ? 'Press release published successfully!'
                : 'Press release unpublished successfully!'
            );
            setStatusModalOpen(false);
            setSelectedStatusUpdate(null);
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };



  const getDropdownActions = useCallback((pressRelease: PressReleaseType): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'Edit',
        onClick: (e) => {
          e.stopPropagation();
          handleEdit(pressRelease.pressReleaseId);
        }
      },
      {
        label: pressRelease.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          handleStatusUpdateClick(pressRelease);
        },
        disabled: isUpdatingStatus
      }
    ];

    // Only show Delete for draft articles
    if (pressRelease.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteClick(pressRelease);
        },
        variant: 'danger' as const,
        disabled: isDeleting
      });
    }

    return actions;
  }, [handleEdit, handleStatusUpdateClick, handleDeleteClick, isUpdatingStatus, isDeleting]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'PUBLISH':
        return { label: 'Published', dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'DRAFT':
        return { label: 'Draft', dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
      default:
        return { label: status, dot: 'bg-blue-400', badge: 'bg-blue-50 text-blue-700 border-blue-200' };
    }
  };

  const columns = useMemo<ColumnDef<PressReleaseType, unknown>[]>(() => [
    {
      id: 'pressRelease',
      header: 'Press Release',
      cell: ({ row }) => {
        const article = row.original;
        const tags = Array.isArray(article.articleTag)
          ? (article.articleTag as string[])
          : typeof article.articleTag === 'string' && article.articleTag
            ? article.articleTag.split(',').map((t) => t.trim()).filter(Boolean)
            : [];
            const language = article.language ?? 'EN';
            const readTime = article.readTimeMinutes ? `${article.readTimeMinutes} min read` : '';
        return (
          <div className="flex items-center gap-3">
            {article.coverImage ? (
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-14 h-12 object-cover rounded-md flex-shrink-0 bg-gray-100"
              />
            ) : (
              <div className="w-14 h-12 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{article.title}</div>
              {/* <div className="text-xs text-gray-400 truncate max-w-xs">{toSlug(article.title)}</div> */}
              <div className="flex items-center gap-2 mt-1">
                   {tags.length > 0 && (
                <div className="flex items-center gap-1  flex-wrap">
                  {tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 text-xs text-gray-500 border border-gray-200 rounded px-1.5 py-0.5">
                      <FolderIcon className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
              
                </div>
              )}
                  <span className="inline-flex items-center text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-1.5 py-0.5 uppercase">{language}</span>
                  {readTime && (
                    <span className="inline-flex items-center text-xs font-semibold border border-gray-200  rounded px-1.5 py-0.5">{readTime}</span>
                  )}
              </div>
           
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4' },
    },
    {
      id: 'author',
      header: 'Author',
      cell: ({ row }) => {
        const article = row.original;
        const name = (article as unknown as Record<string, string>).publishedByName || article.createdByUsername || '';
        const initials = initialsCase(name);
        return name ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{initials}</span>
            </div>
            <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{name}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: ({ row }) => {
        const article = row.original;
        const dateStr = (article as unknown as Record<string, string>).updatedAt || article.datePublished || article.createdAt;
        return dateStr ? (
          <div>
            <div className="text-sm text-gray-900 font-medium">{formatDate(dateStr, 'DD MMM YYYY')}</div>
            <div className="text-xs text-gray-400">{formatDate(dateStr, 'hh:mm A')}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const { label, dot, badge } = getStatusConfig(row.original.status);
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-medium ${badge}`}>
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            {label}
          </span>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const article = row.original;
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Edit"
              onClick={(e) => { e.stopPropagation(); handleEdit(article.pressReleaseId); }}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Preview"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/press-release/${article.pressReleaseId}`); }}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <Eye className="w-4 h-4" />
            </button>
            <div onClick={(e) => e.stopPropagation()}>
              <ActionDropdown
                id={article.pressReleaseId}
                actions={getDropdownActions(article as unknown as PressReleaseType)}
              />
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-6 py-3', cellClassName: 'px-6 py-4 whitespace-nowrap' },
    },
  ], [getDropdownActions, handleEdit, navigate]);

  const handleRowClick = useCallback((row: PressReleaseType) => navigate(`/admin/press-release/${row.pressReleaseId}`), [navigate]);

  const isEmptyState = !isFetching && mode === "default" && pressReleases.length === 0;

  return (
    <>
   
      <div className="space-y-6">

        {/* Press Release Stats */}
        <PressReleaseStats />

        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            onClick={() => navigate('/admin/press-release/new')}
          >
            Create New
          </Button>
        </div>

                                                 {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search ..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

       
        {/* Press Release Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm relative">       
              { isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? <div className="text-center py-20">
                <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                  <img src="/No-post-icon.png" alt="No history" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-semibold text-gray-800 text-lg">
                  {emptyMessage}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'published' ? 'You are yet to create a post' : 'Your draft is empty'}
                </p>
                  <Button variant="primary" className="mt-4" onClick={() => navigate('/admin/press-release/new')}>
                      Create New
                    </Button>
              </div> : (
                <>
                <div className="overflow-x-auto">
                  <TanStackTable
                    data={pressReleases}
                    columns={columns}
                    getRowId={(row) => row.pressReleaseId}
                    onRowClick={handleRowClick}
                    rowClassName="hover:bg-gray-50"
                    emptyMessage={emptyMessage}
                  />
                </div>

                    {/* Pagination controls */}
       
          <div className="flex justify-end">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
                            className="p-4"
            />
          </div>
                </>
              )}
      
        </div>

    
      </div>

         <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Press Release"
        subtitle="Are you sure you want to delete this press release?"
        itemName={selectedPressRelease?.title}
        warningText="This action cannot be undone. The press release will be permanently deleted."
        isLoading={isDeleting}
      />
      {selectedStatusUpdate && (
        <>
          {selectedStatusUpdate.newStatus === 'DRAFT' ? (
            <UnpublishModal
              isOpen={statusModalOpen}
              onClose={() => setStatusModalOpen(false)}
              onConfirm={confirmStatusUpdate}
              itemType="Post"
              isLoading={isUpdatingStatus}
            />
          ) : (
            <PublishModal
              isOpen={statusModalOpen}
              onClose={() => setStatusModalOpen(false)}
              onConfirm={confirmStatusUpdate}
              title="Publish Press Release"
              subtitle="Are you sure you want to publish this press release?"
              itemName={selectedStatusUpdate.title}
              infoText="This press release will be visible to the public once published."
              itemType="Press Release"
              isLoading={isUpdatingStatus}
            />
          )}
        </>
      )}
    </>
  );
};

export default PressRelease;
